<script setup lang="ts">
import { BrandDetailProps } from "./BrandDetail.props"
import {
  VIEW_ITEM_LIST_IDS,
  VIEW_ITEM_LIST_NAMES
} from "~/utils/analytics/viewItemList"

const props = withDefaults(defineProps<BrandDetailProps>(), {
  hasAlgoliaListing: false
})

const route = useRoute()

const aisComponent = ref(null)
const isAisComponentRendered = ref(false)
const { t } = useI18n()

const { setPageInfo } = usePageInfo()

setPageInfo({
  query: "",
  loadPage: props.loadPage,
  listingType: "brand",
  filterString: `brand.slug:${route.params.brand}`,
  pageTitle: t("pages.brandDetail.catalog")
})

const breadcrumbsLinks = computed(() => ({
  links: [
    {
      label: "Home",
      to: "/"
    },
    {
      label: "Marche",
      to: customPaths.brands
    }
  ],
  currentPage: props.brandName
}))

const serverRendered = useState(`server-rendered-${route.path}`, () => false)
onServerPrefetch(() => {
  serverRendered.value = true
})

watch(
  () => aisComponent.value,
  (newValue) => {
    isAisComponentRendered.value = !!newValue
  },
  { immediate: true }
)
</script>

<template>
  <div class="wrapper_brand-detail grid-standard">
    <UtilsBreadcrumbs
      v-bind="breadcrumbsLinks"
      class="negative-margined col-span-full hidden md:block"
    />

    <div class="col-span-full -mx-4">
      <OrganismsMyAccountMobileBackButton
        label="Marche"
        :slug="customPaths.brands"
      />
    </div>

    <div class="negative-margined col-span-full" v-if="hasActivePromotions">
      <BrandPromotionsAlert :brand="brandName" />
    </div>

    <div
      v-if="heroImg"
      class="
        col-span-full
        mb-6
        hidden
        overflow-hidden
        md:relative
        md:flex
        md:aspect-[4/1]
      "
    >
      <UtilsWrapperImage
        v-bind="heroImg"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
        class="
          hero-image__desktop
          absolute
          left-0
          top-1/2
          w-full
          -translate-y-1/2
        "
        sizes="lg:100vw"
      />
    </div>

    <div
      v-if="heroImgMobile"
      class="cover-image__mobile negative-margined col-span-full mb-6 md:hidden"
    >
      <UtilsWrapperImage
        v-bind="heroImgMobile"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
        class="wrapper-image-fixed-240 wrapper-image-full-container-centered"
        sizes="100vw xs:100vw"
      />
    </div>

    <div
      v-if="!hideBrandLogo && brandLogo"
      class="col-span-full mb-10 mt-14 flex justify-center"
      :class="{ 'md:aspect-[4/1]': !!brandLogo?.image?.url }"
    >
      <UtilsWrapperImage
        v-bind="brandLogo"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
        class="brand-logo-wrapper md:column md:flex md:justify-center"
      />
    </div>

    <div
      class="
        products-block-brands-utils-info
        col-span-full
        mb-14
        md:col-span-12
      "
      :class="{ 'mt-10': !brandLogo }"
    >
      <ProductBlocksBrandsUtilsInfo
        v-bind="info"
        :title="brandName"
        :isBrandTitle="true"
        useLinkStyleMarkdown
      />
    </div>

    <div class="col-span-full">
      <DynamicContent
        v-if="content"
        class="cms-content grid-container"
        :components="content"
      />
    </div>

    <div class="negative-margined col-span-full" v-if="!hasAlgoliaListing">
      <AlgoliaListingPage>
        <template #products="slotProps">
          <GAListPageList
            :name="VIEW_ITEM_LIST_NAMES.LISTING"
            :id="VIEW_ITEM_LIST_IDS.BRAND"
          >
            <GridListCategory
              ref="aisComponent"
              :title="$t('pages.brandDetail.catalog')"
              :products-list="slotProps.items"
              :listName="VIEW_ITEM_LIST_NAMES.LISTING"
              :listId="VIEW_ITEM_LIST_IDS.BRAND"
            />
          </GAListPageList>
        </template>
      </AlgoliaListingPage>
    </div>
  </div>
  <TextBlocksCategory v-if="footerText" :description="footerText" />
</template>

<style lang="scss" scoped>
.cms-content {
  :deep(.cms-products-category) {
    @apply col-span-full;
  }

  :deep(.cms-brand-infos) {
    @apply col-span-full;
  }

  :deep(.cms-component-algolia-rails-products) {
    @apply col-span-full mx-[-16px] md:mx-[-71px] lg:mx-[-228px];
  }

  :deep(.cms-wrapper-video) {
    @apply col-span-full;
  }

  :deep(.cms-brand-descriptions) {
    @apply col-span-full;
  }

  :deep(.cms-hero-product) {
    @apply col-span-full;
  }

  :deep(.cms-brand-news) {
    @apply col-span-full;
  }

  :deep(.cms-brand-products-banner) {
    @apply col-span-full;
  }
}
@screen md {
  .brand-logo-wrapper :deep(picture) {
    display: inline-grid;
  }
}
</style>
